<template>
  <div class="animated fadeIn tonnage-basins">
    <b-nav pills>
      <b-nav-item
        :active="segmentSelected === 'Total'"
        v-on:click="segmentSelected = 'Total'"
        class="mr-3 mb-2">
        <strong>Total</strong>
      </b-nav-item>

      <b-nav-item
        v-for="(segmentName, i) in segments"
        :key="`segment-${i}`"
        :active="segmentSelected === segmentName"
        v-on:click="segmentSelected = segmentName"
        class="mr-3 mb-2">
        <strong>{{ segmentName }}</strong>
      </b-nav-item>
    </b-nav>

    <b-row class>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Atlantic"
          :dates="basinStats.atlantic.dates"
          :values="basinStats.atlantic.values"
          :export-types="exportTypes"
          @export="handleExport('atlantic', $event)" />
      </b-col>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Indian Ocean"
          :dates="basinStats.indian_ocean.dates"
          :values="basinStats.indian_ocean.values"
          :exportTypes="exportTypes"
          @export="handleExport('indian_ocean', $event)" />
      </b-col>
    </b-row>
    <b-row class>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Pacific Asia"
          :dates="basinStats.pacific_west.dates"
          :values="basinStats.pacific_west.values"
          :exportTypes="exportTypes"
          @export="handleExport('pacific_west', $event)" />
      </b-col>
      <b-col md="6" sm="12">
        <timeseries-chart
          title="Pacific Americas"
          :dates="basinStats.pacific_east.dates"
          :values="basinStats.pacific_east.values"
          :exportTypes="exportTypes"
          @export="handleExport('pacific_east', $event)" />
      </b-col>
    </b-row>

    <export-modal
      v-model="csvModal.show"
      file-type="csv"
      :file-link="csvModal.link"
      :alert-text="csvModal.alertText" />

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import debounce from 'lodash-es/debounce'
import { default as getNested } from 'lodash-es/get'

import TimeseriesChart from '../components/widgets/TimeseriesChart'
import moment from 'moment'
import { downloadAsFile } from '@/shared/download-utils'
import ExportModal from '../components/widgets/ExportModal'

export default {
  name: 'dashboard',

  components: {
    ExportModal,
    TimeseriesChart,
  },

  data() {
    return {
      segmentSelected: 'Total',
      showPort: true,
      filterParameters: {
        basin: '',
        segment: ''
      },

      segments: [
        'Capesize',
        'Panamax',
        'Supramax',
        'Handysize',
      ],

      basins: [
        'atlantic',
        'indian_ocean',
        'pacific_west',
        'pacific_east'
      ],

      basinStats: {
        atlantic: {},
        indian_ocean: {},
        pacific_west: {},
        pacific_east: {}
      },

      atlanticStats: {},
      indianOceanStats: {},
      eastPacificStats: {},
      westPacificStats: {},

      exportTypes: ['csv'],
      csvModal: {
        show: false,
        link: null,
        alertText: null,
      },
    };
  },

  async mounted() {
      this.applyFilter({segment: this.segmentSelected});
  },

  methods: {
    ...mapActions('api/presets', ['updateCurrentFilterSettings']),
    ...mapActions('api/tonnage', ['getTonnageBalanceBasinStats']),

    applyFilter: debounce(
      async function(filterParameters) {
        return await this.applyFilterHandler(filterParameters);
      },
      400
    ),

    async applyFilterHandler(filterParameters) {
      window.analytics.track('TonnageBasinLoad');
      this.filterParameters = {
        ...this.filterParameters,
        ...filterParameters
      };
      // console.log('updated filter parameters', JSON.stringify(this.filterParameters, null, 2));

      const basinStatPromises = this.basins.map(async basin => {
        const response = await this.getTonnageBalanceBasinStats({
          basin: [basin],
          segment: this.getSegmentValue(),
        });

        this.basinStats[basin] = { dates: [], values: [] };
        getNested(response, 'data.tonnageBasinCounts', []).forEach(item => {
          this.basinStats[basin].dates.push(item.date || '2001-01-01');
          this.basinStats[basin].values.push(item.vesselCount || 0);
        });
      });

      await Promise.all(basinStatPromises);
    },

    async loadTonnageBalanceBasinStats(fieldName) {
      let filterParameters = {
        basin: [fieldName],
        segment: this.getSegmentValue(),
      };
      let response = await this.getTonnageBalanceBasinStats(
        filterParameters
      );
      this.basinStats[fieldName] = getNested(response, 'data', {});
    },

    async handleExport(basin, format) {
      window.analytics.track("TonnageBasinExport");
      let filterParameters = {
        segment: this.getSegmentValue(),
        basin: [basin],
        format,
      };
      // console.log('export', JSON.stringify(filterParameters, null, 2));

      let response = await this.getTonnageBalanceBasinStats(filterParameters);

      let segmentName = 'unknown-segment';
      if(filterParameters.segment.length === this.segments.length) {
        segmentName = 'total';
      } else {
        segmentName = filterParameters.segment.join('-').toLowerCase();
      }

      if(getNested(response, 'data.csv')) {
        this.csvModal.link = response.data.csv
        this.csvModal.alertText = null;
        this.csvModal.show = true;
      } else {
        const timestamp = moment().format('YYYY-MM-DD-HH-mm');
        downloadAsFile(
          response,
          `tonnage_basin__${basin}__${segmentName}_export_${timestamp}.${format}`
        );
      }
    },

    getSegmentValue() {
      if(this.filterParameters.segment === 'Total') {
        return this.segments;
      } else {
        return [this.filterParameters.segment];
      }
    },
  },

  computed: {
    ...mapGetters('api/presets', ['getActivePreset']),
  },

  watch: {
    segmentSelected: async function(val) {
      this.applyFilter({segment: val});

      await this.updateCurrentFilterSettings({
        path: this.$route.path,
        parameters: {
          extraFields: {
            segmentSelected: this.segmentSelected
          },
        },
      });
    },

    getActivePreset: {
      immediate: true,
      handler: function(preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(preset) {
          this.segmentSelected = getNested(preset, 'params.extraFields.segmentSelected', {});
        }
      },
    },
  },
};
</script>
<style scoped>
.tonnage-basins >>> .card-content {
  min-height: 460px;
}
</style>
